import type * as THREE from 'three'
import { collectAssemblyNodeIdentityLookupIds, normalizeAssemblyNodeId } from '../../assembly/utils/AssemblyNodePath'
import { createProjectModelPuppyNodeAliasByNodeId } from '../../export/puppy/projectModelPuppyNodeAliases'
import {
  cloneGlbHierarchyUserDataMeta,
  type GlbHierarchyNode,
  type GlbHierarchyNodeMeta,
} from '../../imported-model/loaders/glb'
import type { ComposedAssemblyResult } from '../../imported-model/loaders/types'
import {
  ImportedModelObjectMetadataService,
  type ImportedModelObjectMetadata,
} from '../../imported-model/metadata'
import {
  rebindImportedModelTopologyRuntimeBodyOccurrences,
} from '../../imported-model/topology/imported-model-topology-runtime'
import {
  type ImportedModelBodyTopologyOccurrenceRebinding,
} from '../../imported-model/topology/ImportedModelTopologyRepository'
import {
  rebaseImportedBodyOccurrenceBindingId,
  resolveImportedBodyOccurrenceNodeId,
} from '../../imported-model/utils'
import { createScopedProjectModelBodyOccurrenceRebindings } from './create-scoped-project-model-body-occurrence-rebindings'
import { resolveProjectModelBodyOccurrenceRebinding } from './utils/resolve-project-model-body-occurrence-rebinding'
import { ProjectModelSubtreeRelationFinalizer } from './ProjectModelSubtreeRelationFinalizer'
import { ProjectModelSubtreeGraphValidator } from './ProjectModelSubtreeGraphValidator'
/**
 * 项目模型新增或替换子树的语义最终化器。
 *
 * 该对象只处理尚未入场的 detached compose 结果，将 hierarchy、Object3D metadata、
 * selection、局部 sceneBindings、topology occurrence 与 Mate 文档统一到同一最终
 * `assemblyNodeId`。它不拥有资源，也不提交 Worker、场景或 Mate Controller。
 */
export class ProjectModelSubtreeSemanticFinalizer {
  /**
   * 将 detached inserted/replacement subtree 的全部装配语义投影统一到最终 occurrence。
   *
   * @param input 当前新增或切换事务提供的操作、显式身份和 detached 新子树。
   * @returns 可直接交给现有 web-client 提交事务的最终子树与 Mate 文档。
   */
  public finalize(
    input: PuppyCAD.FinalizeProjectModelSubtreeSemanticsInput,
  ): PuppyCAD.FinalizedProjectModelSubtreeSemantics {
    const importedResult = input.subtreeImportedResult
    const generatedHierarchy = importedResult.hierarchy
    const sourceSceneBindings = importedResult.sceneBindings
    const aliasByOwnerAssemblyNodeId = this.createPackageAliases(input.packageSemanticScopes)
    const hierarchySlot = input.operation.kind === 'replace'
      ? input.operation.previousHierarchySnapshot
      : this.createInsertedHierarchySlot({
          generatedHierarchy,
          target: input.target,
          parentObjectId: input.operation.parentObjectId,
          parentPath: input.operation.parentPath,
          depth: input.operation.depth,
        })
    if (input.operation.kind === 'insert') {
      this.applyInsertedRootLocalTransform(importedResult.root, input.operation.localTransform)
    }
    const finalizedHierarchy = this.rebaseHierarchy({
      generatedHierarchy,
      replacementRootObject: importedResult.root,
      snapshotHierarchy: hierarchySlot.node,
      parentObjectId: hierarchySlot.parentObjectId,
      target: input.target,
      aliasByOwnerAssemblyNodeId,
    })
    const bodyOccurrenceRebindings = createScopedProjectModelBodyOccurrenceRebindings({
      generatedHierarchy,
      rebasedHierarchy: finalizedHierarchy,
      rootObject: importedResult.root,
      targetNodeType: input.target.nodeType,
      targetAssemblyNodeId: input.target.assemblyNodeId,
    })
    const assemblyNodeIdByObjectId = this.createAssemblyNodeIdByObjectId({
      generatedHierarchy,
      finalizedHierarchy,
      targetAssemblyNodeId: input.target.assemblyNodeId,
      rootObject: importedResult.root,
    })
    const selectionProxyObjectIdByObjectId = this.createSelectionProxyObjectIdByObjectId(
      finalizedHierarchy,
    )
    importedResult.hierarchy = finalizedHierarchy
    this.writeHierarchyMetadata({
      rootObject: importedResult.root,
      hierarchy: finalizedHierarchy,
      fallbackFileId: input.fallbackFileId,
      fallbackAssemblyNodeId: input.target.assemblyNodeId,
      assemblyNodeIdByObjectId,
      selectionProxyObjectIdByObjectId,
    })
    this.rebindObjectMetadata({
      rootObject: importedResult.root,
      targetNodeType: input.target.nodeType,
      fallbackAssemblyNodeId: input.target.assemblyNodeId,
      assemblyNodeIdByObjectId,
      selectionProxyObjectIdByObjectId,
      bodyOccurrenceRebindings,
    })
    rebindImportedModelTopologyRuntimeBodyOccurrences(
      importedResult.root,
      bodyOccurrenceRebindings,
    )
    importedResult.sceneBindings = this.rebuildLocalSceneBindings({
      rootObject: importedResult.root,
      target: input.target,
      packageSemanticScopes: input.packageSemanticScopes,
      sourceSceneBindings,
    })
    importedResult.selectionIndex = this.rebuildLocalSelectionIndex({
      rootObject: importedResult.root,
      sourceSelectionIndex: importedResult.selectionIndex,
      assemblyNodeIdByObjectId,
      targetAssemblyNodeId: input.target.assemblyNodeId,
    })
    const explicitNodeAliases = this.createExplicitTargetAliases(input)
    const relationFinalizer = new ProjectModelSubtreeRelationFinalizer()
    const relationContext: PuppyCAD.ProjectModelSubtreeRelationFinalizationContext = {
      importedResult,
      generatedHierarchy,
      finalizedHierarchy,
      packageSemanticScopes: input.packageSemanticScopes,
      aliasByOwnerAssemblyNodeId,
      explicitNodeAliases,
      bodyOccurrenceRebindings,
    }
    const mateDocuments = relationFinalizer.finalizeMateDocuments(
      input.mateDocuments,
      relationContext,
    )
    const linearComponentPatternDocuments = relationFinalizer
      .finalizeLinearComponentPatternDocuments(
        input.linearComponentPatternDocuments ?? [],
        relationContext,
      )
    new ProjectModelSubtreeGraphValidator().assertClosed({
      importedResult,
      generatedHierarchy,
      finalizedHierarchy,
      targetAssemblyNodeId: input.target.assemblyNodeId,
      explicitNodeAliases,
      bodyOccurrenceRebindings,
      mateDocuments,
      linearComponentPatternDocuments,
    })
    return {
      importedResult,
      mateDocuments,
      linearComponentPatternDocuments,
    }
  }

  /**
   * 按 package owner 创建互相隔离的包内节点映射。
   *
   * @param scopes 本次 replacement 涉及的归档包作用域。
   * @returns owner occurrence 到包内 alias 的映射。
   */
  private createPackageAliases(
    scopes: readonly PuppyCAD.ProjectModelPackageSemanticScope[],
  ): ReadonlyMap<string, Readonly<Record<string, string>>> {
    const aliasByOwnerAssemblyNodeId = new Map<string, Record<string, string>>()
    const ownerNodeKeyByAssemblyNodeId = new Map<string, string>()
    scopes.forEach((scope) => {
      const ownerAssemblyNodeId = this.normalizeNodeId(scope.ownerAssemblyNodeId)
      if (!ownerAssemblyNodeId) {
        throw new Error(`项目模型局部替换归档包缺少 owner occurrence：nodeKey=${scope.ownerNodeKey}`)
      }
      const currentOwnerNodeKey = ownerNodeKeyByAssemblyNodeId.get(ownerAssemblyNodeId)
      if (currentOwnerNodeKey && currentOwnerNodeKey !== scope.ownerNodeKey) {
        throw new Error(
          '项目模型局部替换 package owner occurrence 被多个项目节点认领：'
          + `${ownerAssemblyNodeId} -> ${currentOwnerNodeKey} / ${scope.ownerNodeKey}`,
        )
      }
      ownerNodeKeyByAssemblyNodeId.set(ownerAssemblyNodeId, scope.ownerNodeKey)
      const ownerAliases = aliasByOwnerAssemblyNodeId.get(ownerAssemblyNodeId) ?? {}
      const packageAliases = createProjectModelPuppyNodeAliasByNodeId({
        hierarchy: scope.sourceHierarchy,
        targetRootAssemblyId: ownerAssemblyNodeId,
      })
      Object.entries(packageAliases).forEach(([sourceNodeId, targetNodeId]) => {
        this.setNodeAlias(ownerAliases, sourceNodeId, targetNodeId, scope.ownerNodeKey)
      })
      aliasByOwnerAssemblyNodeId.set(ownerAssemblyNodeId, ownerAliases)
    })
    return aliasByOwnerAssemblyNodeId
  }

  /**
   * 创建当前事务已经明确确认的旧身份到最终 occurrence 映射。
   *
   * @param input Finalizer 输入。
   * @returns 只包含权威结构身份的映射。
   */
  private createExplicitTargetAliases(
    input: PuppyCAD.FinalizeProjectModelSubtreeSemanticsInput,
  ): ReadonlyMap<string, string> {
    const aliases = new Map<string, string>()
    const register = (sourceNodeId: string | null | undefined): void => {
      const sourceNodeIdValue = this.normalizeNodeId(sourceNodeId)
      const targetNodeIdValue = this.normalizeNodeId(input.target.assemblyNodeId)
      if (!sourceNodeIdValue || !targetNodeIdValue || sourceNodeIdValue === targetNodeIdValue) {
        return
      }
      const currentTargetNodeId = aliases.get(sourceNodeIdValue)
      if (currentTargetNodeId && currentTargetNodeId !== targetNodeIdValue) {
        throw new Error(
          `项目模型局部替换显式身份映射冲突：${sourceNodeIdValue} -> ${currentTargetNodeId} / ${targetNodeIdValue}`,
        )
      }
      aliases.set(sourceNodeIdValue, targetNodeIdValue)
    }

    register(input.target.projectNodeKey)
    register(input.target.assemblyDocumentNodeKey)
    if (input.operation.kind === 'replace') {
      register(input.operation.previousHierarchySnapshot.node.meta?.assemblyNodeId)
    }
    return aliases
  }

  /**
   * 为新增节点创建明确的 hierarchy 槽位描述。
   *
   * 该描述来自项目模型已经确认的父级、路径和深度，不借用或伪造旧 replacement 快照。
   *
   * @param input compose 根、目标身份与新增槽位。
   * @returns 可供统一 hierarchy 重锚流程消费的新增槽位。
   */
  private createInsertedHierarchySlot(input: {
    readonly generatedHierarchy: GlbHierarchyNode
    readonly target: PuppyCAD.ProjectModelSubtreeSemanticTarget
    readonly parentObjectId: string | null
    readonly parentPath: string
    readonly depth: number
  }): PuppyCAD.ProjectModelSubtreeHierarchySnapshot {
    const displayName = input.target.displayName.trim()
      || input.generatedHierarchy.name.trim()
      || input.generatedHierarchy.nodeType
      || '未命名节点'
    const parentPath = input.parentPath.trim().replace(/\/+$/u, '')
    return {
      node: {
        ...input.generatedHierarchy,
        parentObjectId: input.parentObjectId,
        name: displayName,
        path: parentPath ? `${parentPath}/${displayName}` : displayName,
        depth: input.depth,
      },
      parentObjectId: input.parentObjectId,
    }
  }

  /**
   * 把项目模型保存的新增节点局部矩阵应用到 detached 场景根。
   *
   * @param root 尚未入场的新增子树根对象。
   * @param localTransform 列主序 4x4 局部矩阵。
   */
  private applyInsertedRootLocalTransform(
    root: THREE.Object3D,
    localTransform: readonly number[],
  ): void {
    if (localTransform.length !== 16 || localTransform.some((value) => !Number.isFinite(value))) {
      throw new Error(`项目模型新增子树局部矩阵无效：length=${localTransform.length}`)
    }
    root.matrix.fromArray(localTransform)
    root.matrix.decompose(root.position, root.quaternion, root.scale)
    if (root.matrixAutoUpdate) {
      root.updateMatrix()
    }
    root.updateMatrixWorld(true)
  }

  /**
   * 将 compose hierarchy 放回旧项目槽位，并在包作用域内重锚后代 occurrence。
   *
   * @param input hierarchy、场景根、旧槽位和包内 alias。
   * @returns 已具备最终项目语义的 replacement hierarchy。
   */
  private rebaseHierarchy(input: {
    readonly generatedHierarchy: GlbHierarchyNode
    readonly replacementRootObject: THREE.Object3D
    readonly snapshotHierarchy: GlbHierarchyNode
    readonly parentObjectId: string | null
    readonly target: PuppyCAD.ProjectModelSubtreeSemanticTarget
    readonly aliasByOwnerAssemblyNodeId: ReadonlyMap<string, Readonly<Record<string, string>>>
  }): GlbHierarchyNode {
    const depthOffset = input.snapshotHierarchy.depth - input.generatedHierarchy.depth
    const sourceRootAssemblyNodeIds = this.collectSourceRootNodeIds(input.generatedHierarchy)
    const rebaseNode = (
      node: GlbHierarchyNode,
      parentObjectId: string | null,
      parentPath: string,
      isRoot: boolean,
      inheritedAliasOwnerAssemblyNodeId: string,
      inheritedAssemblyNodeId: string,
    ): GlbHierarchyNode => {
      const name = isRoot ? input.snapshotHierarchy.name : node.name
      const path = isRoot ? input.snapshotHierarchy.path : `${parentPath}/${name}`
      const sourceAssemblyNodeId = this.normalizeNodeId(node.meta?.assemblyNodeId)
      const aliasOwnerAssemblyNodeId = sourceAssemblyNodeId
        && input.aliasByOwnerAssemblyNodeId.has(sourceAssemblyNodeId)
        ? sourceAssemblyNodeId
        : inheritedAliasOwnerAssemblyNodeId
      const assemblyNodeId = this.resolveHierarchyAssemblyNodeId({
        node,
        targetAssemblyNodeId: input.target.assemblyNodeId,
        targetNodeType: input.target.nodeType,
        sourceRootAssemblyNodeIds,
        aliasByNodeId: input.aliasByOwnerAssemblyNodeId.get(aliasOwnerAssemblyNodeId),
        aliasOwnerAssemblyNodeId,
        isRoot,
        inheritedAssemblyNodeId,
      })
      const meta = node.meta || isRoot
        ? this.rebaseHierarchyMeta(
            node.meta ?? this.createEmptyHierarchyMeta(),
            assemblyNodeId,
            input.target.nodeType === 'part' && !isRoot,
          )
        : undefined

      return {
        ...node,
        parentObjectId,
        name,
        path,
        depth: isRoot ? input.snapshotHierarchy.depth : node.depth + depthOffset,
        meta,
        children: node.children.map((child) => rebaseNode(
          child,
          node.objectId,
          path,
          false,
          aliasOwnerAssemblyNodeId,
          assemblyNodeId,
        )),
      }
    }

    const generatedRootMatchesSceneRoot = input.generatedHierarchy.objectId
      === input.replacementRootObject.uuid
    const normalizedHierarchy = generatedRootMatchesSceneRoot
      ? rebaseNode(
          input.generatedHierarchy,
          input.parentObjectId,
          '',
          true,
          input.target.assemblyNodeId,
          input.target.assemblyNodeId,
        )
      : this.normalizeWrappedHierarchy({
          generatedHierarchy: input.generatedHierarchy,
          targetAssemblyNodeId: input.target.assemblyNodeId,
          targetNodeType: input.target.nodeType,
          aliasByOwnerAssemblyNodeId: input.aliasByOwnerAssemblyNodeId,
        })

    return this.createReplacementHierarchyRoot({
      hierarchy: normalizedHierarchy,
      replacementRootObject: input.replacementRootObject,
      snapshotHierarchy: input.snapshotHierarchy,
      parentObjectId: input.parentObjectId,
      target: input.target,
    })
  }

  /**
   * 归一化 wrapper 场景根内部的真实 hierarchy。
   *
   * @param input compose hierarchy 与包作用域身份。
   * @returns 已重锚 occurrence、尚未挂入 wrapper 的 hierarchy。
   */
  private normalizeWrappedHierarchy(input: {
    readonly generatedHierarchy: GlbHierarchyNode
    readonly targetAssemblyNodeId: string
    readonly targetNodeType: PuppyCAD.ProjectModelSubtreeSemanticTarget['nodeType']
    readonly aliasByOwnerAssemblyNodeId: ReadonlyMap<string, Readonly<Record<string, string>>>
  }): GlbHierarchyNode {
    const sourceRootAssemblyNodeIds = this.collectSourceRootNodeIds(input.generatedHierarchy)
    const visit = (
      node: GlbHierarchyNode,
      isRoot: boolean,
      inheritedAliasOwnerAssemblyNodeId: string,
      inheritedAssemblyNodeId: string,
    ): GlbHierarchyNode => {
      const sourceAssemblyNodeId = this.normalizeNodeId(node.meta?.assemblyNodeId)
      const aliasOwnerAssemblyNodeId = sourceAssemblyNodeId
        && input.aliasByOwnerAssemblyNodeId.has(sourceAssemblyNodeId)
        ? sourceAssemblyNodeId
        : inheritedAliasOwnerAssemblyNodeId
      const assemblyNodeId = this.resolveHierarchyAssemblyNodeId({
        node,
        targetAssemblyNodeId: input.targetAssemblyNodeId,
        targetNodeType: input.targetNodeType,
        sourceRootAssemblyNodeIds,
        aliasByNodeId: input.aliasByOwnerAssemblyNodeId.get(aliasOwnerAssemblyNodeId),
        aliasOwnerAssemblyNodeId,
        isRoot,
        inheritedAssemblyNodeId,
      })
      const meta = node.meta || isRoot
        ? this.normalizeHierarchyMeta(
            node,
            assemblyNodeId,
            isRoot,
            input.targetNodeType === 'part',
          )
        : undefined
      return {
        ...node,
        meta,
        children: node.children.map((child) => visit(
          child,
          false,
          aliasOwnerAssemblyNodeId,
          assemblyNodeId,
        )),
      }
    }

    return visit(
      input.generatedHierarchy,
      true,
      input.targetAssemblyNodeId,
      input.targetAssemblyNodeId,
    )
  }

  /**
   * 为 replacement 场景根创建可插回项目 hierarchy 的根节点。
   *
   * @param input 已重锚 hierarchy、真实场景根和旧槽位。
   * @returns 与真实 replacement root UUID 对齐的 hierarchy 根。
   */
  private createReplacementHierarchyRoot(input: {
    readonly hierarchy: GlbHierarchyNode
    readonly replacementRootObject: THREE.Object3D
    readonly snapshotHierarchy: GlbHierarchyNode
    readonly parentObjectId: string | null
    readonly target: PuppyCAD.ProjectModelSubtreeSemanticTarget
  }): GlbHierarchyNode {
    if (input.hierarchy.objectId === input.replacementRootObject.uuid) {
      return this.alignReplacementRoot({
        hierarchy: input.hierarchy,
        snapshotHierarchy: input.snapshotHierarchy,
        target: input.target,
      })
    }

    const targetDisplayName = input.target.displayName.trim()
      || input.snapshotHierarchy.name
      || input.hierarchy.name
    const rootPath = this.resolveReplacementRootPath(
      input.snapshotHierarchy.path,
      targetDisplayName,
      input.hierarchy.path,
    )
    const semanticKind = this.resolveReplacementRootSemanticKind(
      input.target.nodeType,
      input.snapshotHierarchy.meta?.semanticKind ?? input.hierarchy.meta?.semanticKind,
    )
    const rootMeta: GlbHierarchyNodeMeta = {
      ...(input.snapshotHierarchy.meta ?? this.createEmptyHierarchyMeta()),
      assemblyNodeId: input.target.assemblyNodeId,
      selectionObjectId: input.replacementRootObject.uuid,
      ...(semanticKind ? { semanticKind } : {}),
    }

    return {
      objectId: input.replacementRootObject.uuid,
      parentObjectId: input.parentObjectId,
      name: targetDisplayName,
      path: rootPath,
      nodeType: input.replacementRootObject.type
        || input.snapshotHierarchy.nodeType
        || input.hierarchy.nodeType,
      selectable: true,
      depth: input.snapshotHierarchy.depth,
      meta: rootMeta,
      children: [this.reparentHierarchySubtree(
        input.hierarchy,
        input.replacementRootObject.uuid,
        rootPath,
        input.snapshotHierarchy.depth + 1,
      )],
    }
  }

  /**
   * 对齐真实场景根与替换根 hierarchy 的名称、路径和最终身份。
   *
   * @param input replacement hierarchy、旧槽位和目标身份。
   * @returns 已对齐的 hierarchy 根。
   */
  private alignReplacementRoot(input: {
    readonly hierarchy: GlbHierarchyNode
    readonly snapshotHierarchy: GlbHierarchyNode
    readonly target: PuppyCAD.ProjectModelSubtreeSemanticTarget
  }): GlbHierarchyNode {
    const targetDisplayName = input.target.displayName.trim() || input.hierarchy.name
    const semanticKind = this.resolveReplacementRootSemanticKind(
      input.target.nodeType,
      input.hierarchy.meta?.semanticKind,
    )
    const path = this.resolveReplacementRootPath(
      input.snapshotHierarchy.path,
      targetDisplayName,
      input.hierarchy.path,
    )
    const meta: GlbHierarchyNodeMeta = {
      ...(input.hierarchy.meta ?? this.createEmptyHierarchyMeta()),
      assemblyNodeId: input.target.assemblyNodeId,
      selectionObjectId: input.hierarchy.meta?.selectionObjectId ?? input.hierarchy.objectId,
      ...(semanticKind ? { semanticKind } : {}),
    }
    return {
      ...input.hierarchy,
      name: targetDisplayName,
      path,
      meta,
      children: input.hierarchy.children.map((child) => this.reparentHierarchySubtree(
        child,
        input.hierarchy.objectId,
        path,
        input.hierarchy.depth + 1,
      )),
    }
  }

  /**
   * 递归更新 hierarchy 子树的父对象、路径和深度。
   *
   * @param node 当前 hierarchy 节点。
   * @param parentObjectId 最终父对象 UUID。
   * @param parentPath 最终父路径。
   * @param depth 最终深度。
   * @returns 重定位后的 hierarchy 节点。
   */
  private reparentHierarchySubtree(
    node: GlbHierarchyNode,
    parentObjectId: string,
    parentPath: string,
    depth: number,
  ): GlbHierarchyNode {
    const fallbackName = node.path.split('/').map((segment) => segment.trim()).filter(Boolean).at(-1)
    const name = node.name.trim() || fallbackName || node.nodeType || '未命名节点'
    const path = parentPath ? `${parentPath}/${name}` : name
    return {
      ...node,
      parentObjectId,
      name,
      path,
      depth,
      children: node.children.map((child) => this.reparentHierarchySubtree(
        child,
        node.objectId,
        path,
        depth + 1,
      )),
    }
  }

  /**
   * 计算 hierarchy 节点在最终项目中的 occurrence 身份。
   *
   * @param input 当前节点、目标根和当前 package scope alias。
   * @returns 最终 assemblyNodeId。
   */
  private resolveHierarchyAssemblyNodeId(input: {
    readonly node: GlbHierarchyNode
    readonly targetAssemblyNodeId: string
    readonly targetNodeType: PuppyCAD.ProjectModelSubtreeSemanticTarget['nodeType']
    readonly sourceRootAssemblyNodeIds: readonly string[]
    readonly aliasByNodeId?: Readonly<Record<string, string>>
    readonly aliasOwnerAssemblyNodeId: string
    readonly isRoot: boolean
    readonly inheritedAssemblyNodeId: string
  }): string {
    const targetAssemblyNodeId = this.normalizeNodeId(input.targetAssemblyNodeId)
    // Part 的内部 hierarchy 表达显示分组、Feature Body 或 mesh，不是装配组件 occurrence。
    // 只有 Assembly 的明确子组件才允许在目标根下保留独立装配身份。
    if (input.isRoot || input.targetNodeType === 'part') {
      return targetAssemblyNodeId
    }
    const sourceAssemblyNodeId = this.resolveDeclaredAssemblyNodeId(input.node)
    if (!sourceAssemblyNodeId) {
      const semanticKind = this.resolveSemanticKind(input.node)
      const externalFileUuid = input.node.meta?.externalFileUuid?.trim() ?? ''
      if (semanticKind === 'assembly' || semanticKind === 'part' || externalFileUuid) {
        throw new Error(
          `项目模型局部替换语义边界缺少显式 occurrence 身份：objectId=${input.node.objectId}`,
        )
      }
      return input.inheritedAssemblyNodeId
    }
    if (sourceAssemblyNodeId === targetAssemblyNodeId) {
      return sourceAssemblyNodeId
    }
    const aliasTargetNodeId = this.resolveAliasTarget(
      sourceAssemblyNodeId,
      input.aliasOwnerAssemblyNodeId,
      input.aliasByNodeId,
    )
    if (aliasTargetNodeId) {
      return aliasTargetNodeId
    }
    if (sourceAssemblyNodeId.startsWith(`${targetAssemblyNodeId}/`)) {
      return sourceAssemblyNodeId
    }
    const relativeAssemblyNodeId = this.resolveRelativeNodeId(
      sourceAssemblyNodeId,
      input.sourceRootAssemblyNodeIds,
    )
    return relativeAssemblyNodeId
      ? `${targetAssemblyNodeId}/${relativeAssemblyNodeId}`
      : targetAssemblyNodeId
  }

  /**
   * 按最长 source 前缀解析包内节点 alias。
   *
   * @param sourceAssemblyNodeId 当前包内身份。
   * @param aliasOwnerAssemblyNodeId 当前 package scope owner。
   * @param aliasByNodeId 当前 scope 内的 alias。
   * @returns 命中的最终身份；未命中时为 null。
   */
  private resolveAliasTarget(
    sourceAssemblyNodeId: string,
    aliasOwnerAssemblyNodeId: string,
    aliasByNodeId?: Readonly<Record<string, string>>,
  ): string | null {
    if (!aliasByNodeId) {
      return null
    }
    const normalizedSourceAssemblyNodeId = this.normalizeNodeId(sourceAssemblyNodeId)
    const normalizedOwnerAssemblyNodeId = this.normalizeNodeId(aliasOwnerAssemblyNodeId)
    const sourceCandidates = [
      normalizedSourceAssemblyNodeId,
      ...(normalizedOwnerAssemblyNodeId
        && normalizedSourceAssemblyNodeId.startsWith(`${normalizedOwnerAssemblyNodeId}/`)
        ? [normalizedSourceAssemblyNodeId.slice(normalizedOwnerAssemblyNodeId.length + 1)]
        : []),
    ]
    const aliasEntries = Object.entries(aliasByNodeId)
      .map(([sourceNodeId, targetNodeId]) => ({
        sourceNodeId: this.normalizeNodeId(sourceNodeId),
        targetNodeId: this.normalizeNodeId(targetNodeId),
      }))
      .filter((entry) => entry.sourceNodeId && entry.targetNodeId)
      .sort((left, right) => right.sourceNodeId.length - left.sourceNodeId.length)

    for (const sourceCandidate of sourceCandidates) {
      const matched = aliasEntries.find((entry) => (
        sourceCandidate === entry.sourceNodeId
        || sourceCandidate.startsWith(`${entry.sourceNodeId}/`)
      ))
      if (matched) {
        return sourceCandidate === matched.sourceNodeId
          ? matched.targetNodeId
          : `${matched.targetNodeId}${sourceCandidate.slice(matched.sourceNodeId.length)}`
      }
    }
    return null
  }

  /**
   * 从 source root 身份集合中剥离当前节点的局部路径。
   *
   * @param assemblyNodeId 当前节点身份。
   * @param sourceRootAssemblyNodeIds 包源根身份集合。
   * @returns 相对路径；源根自身返回空字符串。
   */
  private resolveRelativeNodeId(
    assemblyNodeId: string,
    sourceRootAssemblyNodeIds: readonly string[],
  ): string {
    const normalizedAssemblyNodeId = this.normalizeNodeId(assemblyNodeId)
    const matchedRoot = [...sourceRootAssemblyNodeIds]
      .sort((left, right) => right.length - left.length)
      .find((rootNodeId) => (
        normalizedAssemblyNodeId === rootNodeId
        || normalizedAssemblyNodeId.startsWith(`${rootNodeId}/`)
      ))
    if (!matchedRoot || normalizedAssemblyNodeId === matchedRoot) {
      return matchedRoot ? '' : normalizedAssemblyNodeId
    }
    return normalizedAssemblyNodeId.slice(matchedRoot.length + 1)
  }

  /**
   * 收集 compose hierarchy 可能使用的源根身份。
   *
   * @param hierarchy compose hierarchy 根。
   * @returns 去重后的源根身份。
   */
  private collectSourceRootNodeIds(hierarchy: GlbHierarchyNode): readonly string[] {
    return [this.resolveDeclaredAssemblyNodeId(hierarchy)]
      .filter((value) => value.length > 0)
      .filter((value, index, values) => values.indexOf(value) === index)
  }

  /**
   * 从 hierarchy 明确字段读取 occurrence，不使用显示名称或路径推断身份。
   *
   * @param node hierarchy 节点。
   * @returns meta 或唯一 body record 声明的 occurrence；未声明时返回空字符串。
   */
  private resolveDeclaredAssemblyNodeId(node: GlbHierarchyNode): string {
    const metadataAssemblyNodeId = this.normalizeNodeId(node.meta?.assemblyNodeId)
    if (metadataAssemblyNodeId) {
      return metadataAssemblyNodeId
    }
    const bodyOccurrenceNodeIds = new Set((node.meta?.bodyEntries ?? [])
      .map((entry) => this.normalizeNodeId(
        resolveImportedBodyOccurrenceNodeId(entry.bodyBindingId),
      ))
      .filter(Boolean))
    if (bodyOccurrenceNodeIds.size > 1) {
      throw new Error(
        `项目模型局部替换 hierarchy body record occurrence 存在歧义：objectId=${node.objectId}`,
      )
    }
    return [...bodyOccurrenceNodeIds][0] ?? ''
  }

  /**
   * 将 hierarchy metadata 的 body occurrence 重绑到最终节点。
   *
   * @param meta 原始 hierarchy metadata。
   * @param assemblyNodeId 最终 occurrence 身份。
   * @param demoteComponentSemantic 是否把内部组件语义降为展示语义。
   * @returns 与最终身份一致的新 metadata。
   */
  private rebaseHierarchyMeta(
    meta: GlbHierarchyNodeMeta | undefined,
    assemblyNodeId: string,
    demoteComponentSemantic = false,
  ): GlbHierarchyNodeMeta | undefined {
    if (!meta) {
      return undefined
    }
    const bodyEntries = meta.bodyEntries?.map((entry) => ({
      ...entry,
      bodyBindingId: rebaseImportedBodyOccurrenceBindingId(entry.bodyBindingId, assemblyNodeId),
    }))
    const rebasedMeta: GlbHierarchyNodeMeta = {
      ...cloneGlbHierarchyUserDataMeta(meta),
      assemblyNodeId,
      ...(bodyEntries ? { bodyEntries, bodyCount: bodyEntries.length } : {}),
    }
    if (
      demoteComponentSemantic
      && (rebasedMeta.semanticKind === 'assembly' || rebasedMeta.semanticKind === 'part')
    ) {
      /**
       * 一个项目零件 occurrence 只能有一个可写组件根。包内结构 Group、显示 Mesh 即使继承了
       * 原文件的 part/assembly 声明，也只是该零件的展示后代，不能再次成为拖拽 transform 根。
       */
      rebasedMeta.semanticKind = 'unknown'
    }
    return rebasedMeta
  }

  /**
   * 清理 wrapper 内 hierarchy 的交互锚点并写入最终身份。
   *
   * @param node 当前 hierarchy 节点。
   * @param assemblyNodeId 最终 occurrence。
   * @param isRoot 是否为内部 hierarchy 根。
   * @param demoteComponentSemantic 是否把当前包装层级视为零件根的内部展示结构。
   * @returns 可挂入 replacement wrapper 的 metadata。
   */
  private normalizeHierarchyMeta(
    node: GlbHierarchyNode,
    assemblyNodeId: string,
    isRoot: boolean,
    demoteComponentSemantic: boolean,
  ): GlbHierarchyNodeMeta {
    const meta = this.rebaseHierarchyMeta(
      node.meta ?? this.createEmptyHierarchyMeta(),
      assemblyNodeId,
      demoteComponentSemantic,
    ) as GlbHierarchyNodeMeta
    if (isRoot) {
      meta.selectionObjectId = node.objectId
    }
    return meta
  }

  /**
   * 创建不承载几何的 hierarchy 元数据初始值。
   *
   * 每次返回独立数组，避免不同运行时节点共享可变的材质名称集合。
   *
   * @returns 可安全写入 wrapper 或缺失元数据节点的完整初始值。
   */
  private createEmptyHierarchyMeta(): GlbHierarchyNodeMeta {
    return {
      hasGeometry: false,
      hasDirectGeometry: false,
      materialNames: [],
    }
  }

  /**
   * 建立 Object3D UUID 到最终 occurrence 的显式映射。
   *
   * @param input generated/final hierarchy 与 compose sceneBindings。
   * @returns hierarchy 和 display mesh 共用的对象身份映射。
   */
  private createAssemblyNodeIdByObjectId(input: {
    readonly generatedHierarchy: GlbHierarchyNode
    readonly finalizedHierarchy: GlbHierarchyNode
    readonly targetAssemblyNodeId: string
    readonly rootObject: THREE.Object3D
  }): ReadonlyMap<string, string> {
    const assemblyNodeIdByObjectId = new Map<string, string>()
    const generatedNodeByObjectId = new Map<string, GlbHierarchyNode>()
    const targetsBySourceAssemblyNodeId = new Map<string, Set<string>>()
    const indexGenerated = (node: GlbHierarchyNode): void => {
      generatedNodeByObjectId.set(node.objectId, node)
      node.children.forEach(indexGenerated)
    }
    indexGenerated(input.generatedHierarchy)

    const visit = (node: GlbHierarchyNode, inheritedAssemblyNodeId: string): void => {
      const currentAssemblyNodeId = this.normalizeNodeId(node.meta?.assemblyNodeId)
      const assemblyNodeId = node === input.finalizedHierarchy
        ? input.targetAssemblyNodeId
        : currentAssemblyNodeId || inheritedAssemblyNodeId
      assemblyNodeIdByObjectId.set(node.objectId, assemblyNodeId)
      const sourceAssemblyNodeId = this.normalizeNodeId(
        generatedNodeByObjectId.get(node.objectId)?.meta?.assemblyNodeId,
      )
      if (sourceAssemblyNodeId) {
        const targets = targetsBySourceAssemblyNodeId.get(sourceAssemblyNodeId) ?? new Set<string>()
        targets.add(assemblyNodeId)
        targetsBySourceAssemblyNodeId.set(sourceAssemblyNodeId, targets)
      }
      node.children.forEach((child) => visit(child, assemblyNodeId))
    }
    visit(input.finalizedHierarchy, input.targetAssemblyNodeId)

    const hierarchyObjectIds = new Set(assemblyNodeIdByObjectId.keys())
    const finalAssemblyNodeIds = new Set(assemblyNodeIdByObjectId.values())
    const visitObject = (object: THREE.Object3D, inheritedAssemblyNodeId: string): void => {
      const hierarchyAssemblyNodeId = assemblyNodeIdByObjectId.get(object.uuid)
      const sourceAssemblyNodeId = this.normalizeNodeId(
        ImportedModelObjectMetadataService.readOwnMetadata(object)?.assemblyNodeId,
      )
      const sourceTargets = sourceAssemblyNodeId
        ? targetsBySourceAssemblyNodeId.get(sourceAssemblyNodeId)
        : undefined
      const sourceTarget = sourceTargets?.size === 1
        ? [...sourceTargets][0]
        : sourceTargets?.has(inheritedAssemblyNodeId)
          ? inheritedAssemblyNodeId
          : sourceAssemblyNodeId && finalAssemblyNodeIds.has(sourceAssemblyNodeId)
            ? sourceAssemblyNodeId
            : ''
      const assemblyNodeId = hierarchyAssemblyNodeId
        ?? sourceTarget
        ?? inheritedAssemblyNodeId
      if (!hierarchyObjectIds.has(object.uuid)) {
        assemblyNodeIdByObjectId.set(object.uuid, assemblyNodeId || inheritedAssemblyNodeId)
      }
      object.children.forEach((child) => visitObject(
        child,
        assemblyNodeId || inheritedAssemblyNodeId,
      ))
    }
    visitObject(input.rootObject, input.targetAssemblyNodeId)
    return assemblyNodeIdByObjectId
  }

  /**
   * 为 hierarchy 对象建立稳定选择代理映射。
   *
   * @param hierarchy 最终 replacement hierarchy。
   * @returns Object3D UUID 到组件选择代理 UUID 的映射。
   */
  private createSelectionProxyObjectIdByObjectId(
    hierarchy: GlbHierarchyNode,
  ): ReadonlyMap<string, string> {
    const selectionProxyObjectIdByObjectId = new Map<string, string>()
    const visit = (node: GlbHierarchyNode, inheritedProxyObjectId: string): void => {
      const proxyObjectId = node === hierarchy
        ? hierarchy.objectId
        : this.resolveChildSelectionBoundary(hierarchy, node) || inheritedProxyObjectId
      selectionProxyObjectIdByObjectId.set(node.objectId, proxyObjectId)
      node.children.forEach((child) => visit(child, proxyObjectId))
    }
    visit(hierarchy, hierarchy.objectId)
    return selectionProxyObjectIdByObjectId
  }

  /**
   * 把最终 hierarchy 写回真实 replacement Object3D metadata。
   *
   * @param input 对象根、最终 hierarchy 和显式对象映射。
   */
  private writeHierarchyMetadata(input: {
    readonly rootObject: THREE.Object3D
    readonly hierarchy: GlbHierarchyNode
    readonly fallbackFileId: string
    readonly fallbackAssemblyNodeId: string
    readonly assemblyNodeIdByObjectId: ReadonlyMap<string, string>
    readonly selectionProxyObjectIdByObjectId: ReadonlyMap<string, string>
  }): void {
    const objectsByUuid = new Map<string, THREE.Object3D>()
    input.rootObject.traverse((object) => objectsByUuid.set(object.uuid, object))
    const visit = (node: GlbHierarchyNode): void => {
      const object = objectsByUuid.get(node.objectId)
      if (object) {
        const currentMetadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
        const fileId = currentMetadata?.fileId ?? input.fallbackFileId
        const assemblyNodeId = input.assemblyNodeIdByObjectId.get(node.objectId)
          ?? currentMetadata?.assemblyNodeId
          ?? input.fallbackAssemblyNodeId
        const selectionProxyObjectId = input.selectionProxyObjectIdByObjectId.get(node.objectId)
        ImportedModelObjectMetadataService.writeMetadata(object, {
          fileId,
          hierarchy: {
            fileId,
            name: node.name,
            path: node.path,
            nodeType: node.nodeType,
            selectable: node.selectable,
            parentObjectId: node.parentObjectId,
            depth: node.depth,
            meta: node.meta ? cloneGlbHierarchyUserDataMeta(node.meta) : undefined,
          },
          selectable: node.selectable,
          assemblyNodeId,
          ...(selectionProxyObjectId ? { selectionProxyObjectId } : {}),
          selectionTarget: {
            ...(currentMetadata?.selectionTarget ?? {}),
            assemblyNodeId,
          },
        })
      }
      node.children.forEach(visit)
    }
    visit(input.hierarchy)
  }

  /**
   * 将 hierarchy 外 display mesh 的 metadata 与 body occurrence 一并重绑。
   *
   * @param input replacement 对象树和本次唯一 body 映射。
   */
  private rebindObjectMetadata(input: {
    readonly rootObject: THREE.Object3D
    readonly targetNodeType: PuppyCAD.ProjectModelSubtreeSemanticTarget['nodeType']
    readonly fallbackAssemblyNodeId: string
    readonly assemblyNodeIdByObjectId: ReadonlyMap<string, string>
    readonly selectionProxyObjectIdByObjectId: ReadonlyMap<string, string>
    readonly bodyOccurrenceRebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[]
  }): void {
    const selectionProxyByAssemblyNodeId = new Map<string, string>()
    input.assemblyNodeIdByObjectId.forEach((assemblyNodeId, objectId) => {
      const proxyObjectId = input.selectionProxyObjectIdByObjectId.get(objectId)
      if (proxyObjectId && !selectionProxyByAssemblyNodeId.has(assemblyNodeId)) {
        selectionProxyByAssemblyNodeId.set(assemblyNodeId, proxyObjectId)
      }
    })

    const collectOwnBodyBindingIds = (
      object: THREE.Object3D,
      metadata: ImportedModelObjectMetadata | undefined,
    ): string[] => {
      const entries = [
        ...(Array.isArray(object.userData?.importedPartBodyEntries)
          ? object.userData.importedPartBodyEntries
          : []),
        ...(Array.isArray(object.userData?.primitiveEntityEntries)
          ? object.userData.primitiveEntityEntries
          : []),
      ]
      return Array.from(new Set([
        ...entries.flatMap((entry) => (
          entry && typeof entry === 'object' && !Array.isArray(entry)
            && typeof (entry as Record<string, unknown>).bodyBindingId === 'string'
            ? [(entry as Record<string, string>).bodyBindingId]
            : []
        )),
        ...Object.keys(metadata?.bodyBindingSelectionTargetsById ?? {}),
      ]))
    }

    const rebindUserDataEntries = (
      entries: unknown,
      sourceObject: THREE.Object3D,
    ): unknown[] | undefined => {
      if (!Array.isArray(entries)) {
        return undefined
      }
      return entries.map((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
          return entry
        }
        const sourceEntry = entry as Record<string, unknown>
        const sourceBodyBindingId = typeof sourceEntry.bodyBindingId === 'string'
          ? sourceEntry.bodyBindingId
          : ''
        const rebinding = resolveProjectModelBodyOccurrenceRebinding(
          sourceBodyBindingId,
          input.bodyOccurrenceRebindings,
          sourceObject,
        )
        return rebinding
          ? { ...sourceEntry, bodyBindingId: rebinding.targetBodyBindingId }
          : { ...sourceEntry }
      })
    }

    const rebindBodySelectionTargets = (
      targets: ImportedModelObjectMetadata['bodyBindingSelectionTargetsById'],
      bodyBindingIds: readonly string[],
      sourceObject: THREE.Object3D,
    ): ImportedModelObjectMetadata['bodyBindingSelectionTargetsById'] => {
      const reboundTargets: NonNullable<ImportedModelObjectMetadata['bodyBindingSelectionTargetsById']> = {}
      Object.entries(targets ?? {}).forEach(([bodyBindingId, target]) => {
        const rebinding = resolveProjectModelBodyOccurrenceRebinding(
          bodyBindingId,
          input.bodyOccurrenceRebindings,
          sourceObject,
        )
        reboundTargets[rebinding?.targetBodyBindingId ?? bodyBindingId] = rebinding
          ? { ...target, assemblyNodeId: rebinding.targetAssemblyNodeId }
          : { ...target }
      })
      bodyBindingIds.forEach((bodyBindingId) => {
        const rebinding = resolveProjectModelBodyOccurrenceRebinding(
          bodyBindingId,
          input.bodyOccurrenceRebindings,
          sourceObject,
        )
        if (rebinding && !reboundTargets[rebinding.targetBodyBindingId]) {
          reboundTargets[rebinding.targetBodyBindingId] = {
            assemblyNodeId: rebinding.targetAssemblyNodeId,
          }
        }
      })
      return Object.keys(reboundTargets).length > 0 ? reboundTargets : undefined
    }

    const visit = (
      object: THREE.Object3D,
      inheritedAssemblyNodeId: string,
      inheritedSelectionProxyObjectId: string,
    ): void => {
      const metadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
      const bodyBindingIds = collectOwnBodyBindingIds(object, metadata)
      const bodyOwnerAssemblyNodeIds = new Set(bodyBindingIds
        .map((bodyBindingId) => resolveProjectModelBodyOccurrenceRebinding(
          bodyBindingId,
          input.bodyOccurrenceRebindings,
          object,
        )?.targetAssemblyNodeId ?? '')
        .filter(Boolean))
      if (bodyOwnerAssemblyNodeIds.size > 1) {
        throw new Error(`项目模型局部替换 Object3D 包含多个 body owner：objectId=${object.uuid}`)
      }
      const bodyAssemblyNodeId = [...bodyOwnerAssemblyNodeIds][0]
      const assemblyNodeId = bodyAssemblyNodeId
        ?? input.assemblyNodeIdByObjectId.get(object.uuid)
        ?? inheritedAssemblyNodeId
      const selectionProxyObjectId = input.selectionProxyObjectIdByObjectId.get(object.uuid)
        ?? selectionProxyByAssemblyNodeId.get(assemblyNodeId)
        ?? inheritedSelectionProxyObjectId
      const importedPartBodyEntries = rebindUserDataEntries(
        object.userData?.importedPartBodyEntries,
        object,
      )
      const primitiveEntityEntries = rebindUserDataEntries(
        object.userData?.primitiveEntityEntries,
        object,
      )
      if (importedPartBodyEntries || primitiveEntityEntries) {
        object.userData = {
          ...object.userData,
          ...(importedPartBodyEntries ? { importedPartBodyEntries } : {}),
          ...(primitiveEntityEntries ? { primitiveEntityEntries } : {}),
        }
      }
      if (metadata || bodyAssemblyNodeId) {
        ImportedModelObjectMetadataService.writeMetadata(object, {
          ...(metadata ?? {}),
          hierarchy: metadata?.hierarchy
            ? {
                ...metadata.hierarchy,
                meta: this.rebaseHierarchyMeta(
                  metadata.hierarchy.meta,
                  assemblyNodeId,
                  input.targetNodeType === 'part' && object !== input.rootObject,
                ),
              }
            : undefined,
          assemblyNodeId,
          selectionProxyObjectId,
          selectionTarget: metadata?.selectionTarget
            ? { ...metadata.selectionTarget, assemblyNodeId }
            : bodyAssemblyNodeId
              ? { assemblyNodeId }
              : undefined,
          bodyBindingSelectionTargetsById: rebindBodySelectionTargets(
            metadata?.bodyBindingSelectionTargetsById,
            bodyBindingIds,
            object,
          ),
        })
      }
      object.children.forEach((child) => visit(
        child,
        assemblyNodeId,
        selectionProxyObjectId,
      ))
    }

    visit(input.rootObject, input.fallbackAssemblyNodeId, input.rootObject.uuid)
  }

  /**
   * 从最终 Object3D metadata 重建 replacement 子树自己的场景绑定。
   *
   * 设计说明：compose 阶段的 `byAssemblyNodeId` 仍属于包内临时身份，必须根据最终
   * Object3D metadata 重建；`byProjectNodeKey` 则来自 ModelAssetNode 结构遍历，是包内物化
   * 后代与项目节点的唯一结构归属，最终化只能校验并保留，不能用 package owner 集合替代。
   * 这里同时遍历已经完成身份写回的 detached 对象树，确保 hierarchy 外的 display mesh
   * 也进入最终 occurrence 索引。
   *
   * @param input 最终对象树、目标项目节点与归档包 owner 作用域。
   * @returns 只包含 replacement 子树对象的局部场景绑定。
   */
  private rebuildLocalSceneBindings(input: {
    readonly rootObject: THREE.Object3D
    readonly target: PuppyCAD.ProjectModelSubtreeSemanticTarget
    readonly packageSemanticScopes: readonly PuppyCAD.ProjectModelPackageSemanticScope[]
    readonly sourceSceneBindings: ComposedAssemblyResult['sceneBindings']
  }): ComposedAssemblyResult['sceneBindings'] {
    const projectNodeKeyByAssemblyNodeId = new Map<string, string>()
    /** 登记最终 occurrence 到项目结构节点的唯一归属。 */
    const registerProjectNode = (assemblyNodeId: string, projectNodeKey: string): void => {
      const normalizedAssemblyNodeId = this.normalizeNodeId(assemblyNodeId)
      if (!normalizedAssemblyNodeId || !projectNodeKey) {
        return
      }
      const currentProjectNodeKey = projectNodeKeyByAssemblyNodeId.get(normalizedAssemblyNodeId)
      if (currentProjectNodeKey && currentProjectNodeKey !== projectNodeKey) {
        throw new Error(
          `项目模型局部替换场景绑定 owner 冲突：${normalizedAssemblyNodeId} -> ${currentProjectNodeKey} / ${projectNodeKey}`,
        )
      }
      projectNodeKeyByAssemblyNodeId.set(normalizedAssemblyNodeId, projectNodeKey)
    }
    registerProjectNode(input.target.assemblyNodeId, input.target.projectNodeKey)
    input.packageSemanticScopes.forEach((scope) => {
      registerProjectNode(scope.ownerAssemblyNodeId, scope.ownerNodeKey)
    })

    const byAssemblyNodeId: ComposedAssemblyResult['sceneBindings']['byAssemblyNodeId'] = {}
    const byProjectNodeKey: NonNullable<ComposedAssemblyResult['sceneBindings']['byProjectNodeKey']> = {}
    const byObject3DUuid: ComposedAssemblyResult['sceneBindings']['byObject3DUuid'] = {}
    const projectNodeKeyByBoundObjectId = new Map<string, string>()
    /** 向普通索引追加唯一对象 UUID。 */
    const appendObjectId = (index: Record<string, string[]>, key: string, objectId: string): void => {
      const objectIds = index[key] ?? []
      if (!objectIds.includes(objectId)) {
        index[key] = [...objectIds, objectId]
      }
    }
    /** 登记 Object3D 的唯一项目结构归属，禁止两个 ModelAssetNode 认领同一运行对象。 */
    const appendProjectObjectId = (projectNodeKey: string, objectId: string): void => {
      const currentProjectNodeKey = projectNodeKeyByBoundObjectId.get(objectId)
      if (currentProjectNodeKey && currentProjectNodeKey !== projectNodeKey) {
        throw new Error(
          '项目模型局部替换 Object3D 被多个项目节点认领：'
          + `objectId=${objectId}, firstNodeKey=${currentProjectNodeKey}, duplicateNodeKey=${projectNodeKey}`,
        )
      }
      projectNodeKeyByBoundObjectId.set(objectId, projectNodeKey)
      appendObjectId(byProjectNodeKey, projectNodeKey, objectId)
    }
    /** 遍历最终 detached 对象树并重建运行 occurrence 与对象索引。 */
    const visit = (object: THREE.Object3D, inheritedAssemblyNodeId: string): void => {
      const metadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
      const assemblyNodeId = this.normalizeNodeId(metadata?.assemblyNodeId)
        || inheritedAssemblyNodeId
      byObject3DUuid[object.uuid] = object
      appendObjectId(byAssemblyNodeId, assemblyNodeId, object.uuid)
      const projectNodeKey = projectNodeKeyByAssemblyNodeId.get(assemblyNodeId)
      if (projectNodeKey) {
        appendProjectObjectId(projectNodeKey, object.uuid)
      }
      object.children.forEach((child) => visit(child, assemblyNodeId))
    }
    visit(input.rootObject, this.normalizeNodeId(input.target.assemblyNodeId))

    const sourceProjectBindings = input.sourceSceneBindings.byProjectNodeKey ?? {}
    const sourceRootProjectNodeKeys = Object.entries(sourceProjectBindings)
      .filter(([, objectIds]) => objectIds.includes(input.rootObject.uuid))
      .map(([projectNodeKey]) => projectNodeKey)
    if (sourceRootProjectNodeKeys.length > 1) {
      throw new Error(
        '项目模型局部替换根对象被多个项目节点认领：'
        + `objectId=${input.rootObject.uuid}, nodeKeys=${sourceRootProjectNodeKeys.join(',')}`,
      )
    }
    const sourceRootProjectNodeKey = sourceRootProjectNodeKeys[0]
    Object.entries(sourceProjectBindings).forEach(([sourceProjectNodeKey, objectIds]) => {
      // replacement 根继承当前项目槽位；其余后代继续使用 compose 从 ModelAssetNode 建立的稳定 key。
      const projectNodeKey = sourceProjectNodeKey === sourceRootProjectNodeKey
        ? input.target.projectNodeKey
        : sourceProjectNodeKey
      objectIds.forEach((objectId) => {
        if (!byObject3DUuid[objectId]) {
          // compose 绑定可能仍包含已从 detached replacement 移除的辅助对象；局部结果只发布真实子树成员。
          return
        }
        appendProjectObjectId(projectNodeKey, objectId)
      })
    })

    return {
      byAssemblyNodeId,
      byProjectNodeKey,
      byObject3DUuid,
    }
  }

  /**
   * 从最终 Object3D metadata 重建 replacement 子树的选择索引。
   *
   * compose 阶段已经登记的 primitive/face 字段会保留；最终 metadata 负责补齐漏登记的
   * hierarchy 与 body 对象，并覆盖为最终 occurrence。对象不在 detached 子树中时不会进入结果。
   *
   * @param input 最终对象树、compose 选择索引与最终对象身份映射。
   * @returns 只引用当前 detached 子树对象的选择索引。
   */
  private rebuildLocalSelectionIndex(input: {
    readonly rootObject: THREE.Object3D
    readonly sourceSelectionIndex: ComposedAssemblyResult['selectionIndex']
    readonly assemblyNodeIdByObjectId: ReadonlyMap<string, string>
    readonly targetAssemblyNodeId: string
  }): ComposedAssemblyResult['selectionIndex'] {
    const byObject3DUuid: ComposedAssemblyResult['selectionIndex']['byObject3DUuid'] = {}
    input.rootObject.traverse((object) => {
      const sourceTarget = input.sourceSelectionIndex.byObject3DUuid[object.uuid]
      const metadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
      const shouldRegister = Boolean(
        sourceTarget
        || metadata?.selectable
        || metadata?.selectionTarget
        || metadata?.hierarchy?.selectable,
      )
      if (!shouldRegister) {
        return
      }
      const assemblyNodeId = this.normalizeNodeId(metadata?.selectionTarget?.assemblyNodeId)
        || this.normalizeNodeId(metadata?.assemblyNodeId)
        || this.normalizeNodeId(input.assemblyNodeIdByObjectId.get(object.uuid))
        || (object === input.rootObject
          ? this.normalizeNodeId(input.targetAssemblyNodeId)
          : '')
      if (!assemblyNodeId) {
        throw new Error(
          `项目模型局部替换可选择对象缺少最终 occurrence：objectId=${object.uuid}`,
        )
      }
      byObject3DUuid[object.uuid] = {
        ...(sourceTarget ?? {}),
        ...(metadata?.selectionTarget ?? {}),
        assemblyNodeId,
      }
    })
    return { byObject3DUuid }
  }

  /**
   * 判断子 hierarchy 节点是否形成独立组件选择边界。
   *
   * @param rootHierarchy replacement hierarchy 根。
   * @param node 当前节点。
   * @returns 独立边界对象 UUID；普通内部节点返回空字符串。
   */
  private resolveChildSelectionBoundary(
    rootHierarchy: GlbHierarchyNode,
    node: GlbHierarchyNode,
  ): string {
    if (node === rootHierarchy || this.resolveSemanticKind(rootHierarchy) !== 'assembly') {
      return ''
    }
    const semanticKind = this.resolveSemanticKind(node)
    const externalFileUuid = node.meta?.externalFileUuid?.trim() ?? ''
    return semanticKind === 'assembly' || semanticKind === 'part' || externalFileUuid
      ? node.objectId
      : ''
  }

  /**
   * 解析 hierarchy 节点语义类型。
   *
   * @param node hierarchy 节点。
   * @returns 规范化后的语义类型。
   */
  private resolveSemanticKind(node: GlbHierarchyNode): string {
    return node.meta?.semanticKind?.trim() ?? ''
  }

  /**
   * 解析 replacement root 在设计树中的最终路径。
   *
   * @param snapshotPath 旧槽位路径。
   * @param targetDisplayName 目标显示名称。
   * @param fallbackPath 新 hierarchy 原始路径。
   * @returns 只替换目标根名称后的路径。
   */
  private resolveReplacementRootPath(
    snapshotPath: string,
    targetDisplayName: string,
    fallbackPath: string,
  ): string {
    const displayName = targetDisplayName.trim()
    if (!displayName) {
      return fallbackPath
    }
    const segments = snapshotPath.split('/').map((segment) => segment.trim()).filter(Boolean)
    const existingIndex = segments.indexOf(displayName)
    if (existingIndex >= 0) {
      return segments.slice(0, existingIndex + 1).join('/')
    }
    const parentPath = segments.slice(0, -1).join('/')
    return parentPath ? `${parentPath}/${displayName}` : displayName
  }

  /**
   * 解析 replacement root 的装配语义类型。
   *
   * @param nodeType 项目节点类型。
   * @param fallbackSemanticKind compose hierarchy 语义。
   * @returns part/assembly 语义；未知时返回 undefined。
   */
  private resolveReplacementRootSemanticKind(
    nodeType: PuppyCAD.ProjectModelSubtreeSemanticTarget['nodeType'],
    fallbackSemanticKind: GlbHierarchyNodeMeta['semanticKind'] | undefined,
  ): GlbHierarchyNodeMeta['semanticKind'] | undefined {
    if (nodeType === 'assembly' || nodeType === 'part') {
      return nodeType
    }
    return fallbackSemanticKind === 'assembly' || fallbackSemanticKind === 'part'
      ? fallbackSemanticKind
      : undefined
  }

  /**
   * 写入包内 alias，并拒绝同一 source 指向不同 occurrence。
   *
   * @param aliasByNodeId 当前 package scope alias。
   * @param sourceNodeId 包内源身份。
   * @param targetNodeId 最终项目身份。
   * @param ownerNodeKey 错误上下文中的 package owner。
   */
  private setNodeAlias(
    aliasByNodeId: Record<string, string>,
    sourceNodeId: string,
    targetNodeId: string,
    ownerNodeKey: string,
  ): void {
    const sourceNodeIdValue = this.normalizeNodeId(sourceNodeId)
    const targetNodeIdValue = this.normalizeNodeId(targetNodeId)
    if (!sourceNodeIdValue || !targetNodeIdValue || sourceNodeIdValue === targetNodeIdValue) {
      return
    }
    const currentTargetNodeId = aliasByNodeId[sourceNodeIdValue]
    if (currentTargetNodeId && currentTargetNodeId !== targetNodeIdValue) {
      throw new Error(
        `项目模型归档包节点 alias 冲突：owner=${ownerNodeKey}, ${sourceNodeIdValue} -> ${currentTargetNodeId} / ${targetNodeIdValue}`,
      )
    }
    aliasByNodeId[sourceNodeIdValue] = targetNodeIdValue
  }

  /**
   * 判断两个 occurrence 身份在规范化与安全解码后是否等价。
   *
   * @param left 左侧节点身份。
   * @param right 右侧节点身份。
   * @returns 任一稳定查找形式相同时返回 true。
   */
  private areNodeIdsEquivalent(left: string, right: string): boolean {
    const leftLookupIds = new Set(collectAssemblyNodeIdentityLookupIds(
      this.normalizeNodeId(left),
    ))
    return collectAssemblyNodeIdentityLookupIds(this.normalizeNodeId(right))
      .some((lookupId) => leftLookupIds.has(lookupId))
  }

  /**
   * 规范化装配节点身份。
   *
   * @param value 待规范化身份。
   * @returns 可比较和拼接的节点 ID。
   */
  private normalizeNodeId(value: unknown): string {
    return typeof value === 'string' ? normalizeAssemblyNodeId(value).trim() : ''
  }

}
